import { GalleryMediaKind, VolunteersGalleryRules } from "../context/VolunteersGalleryContext/constants";
import { hasPictureSignature } from "./pictureFile";

const MEGABYTE = 1024 * 1024;

// The type the backend expects for each extension it accepts; it checks the file's content against this type.
const MIME_TYPES: Record<string, string> = {
	jpg: "image/jpeg",
	jpeg: "image/jpeg",
	png: "image/png",
	gif: "image/gif",
	mp4: "video/mp4",
};

// The MP4 starts the backend accepts (FileSignatureValidator): an "ftyp" box of 20, 24, 28 or 32 bytes.
const VIDEO_SIGNATURES = [0x14, 0x18, 0x1c, 0x20].map(boxSize => [0x00, 0x00, 0x00, boxSize, 0x66, 0x74, 0x79, 0x70]);

// A browser that cannot read a video's header in this time cannot play it either.
const VIDEO_CHECK_TIMEOUT = 15000;

// Like the backend's Path.GetExtension: what follows the last dot, nothing when the name has no dot.
const getExtension = (fileName: string) => {
	const dot = fileName.lastIndexOf(".");
	return dot >= 0 ? fileName.slice(dot + 1).toLowerCase() : "";
};

export const getGalleryMediaKind = (fileName: string): GalleryMediaKind | null => {
	const extension = getExtension(fileName);
	if ((VolunteersGalleryRules.pictureExtensions as readonly string[]).includes(extension)) {
		return "picture";
	}
	return (VolunteersGalleryRules.videoExtensions as readonly string[]).includes(extension) ? "video" : null;
};

// The API tells pictures and videos apart only by the extension of the address.
export const isGalleryVideoUrl = (url: string | null | undefined): boolean => !!url && /\.mp4$/i.test(url.split(/[?#]/)[0]);

export const formatFileSize = (bytes: number) => `${(bytes / MEGABYTE).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} MB`;

export const GALLERY_ACCEPT = [
	...VolunteersGalleryRules.pictureExtensions.map(extension => `.${extension}`),
	...VolunteersGalleryRules.videoExtensions.map(extension => `.${extension}`),
	"image/jpeg",
	"image/png",
	"image/gif",
	"video/mp4",
].join(",");

// Some systems give an .mp4 or .jpg no type, or an unusual one; the backend refuses those. The type follows the
// extension, and the content check below makes sure the content matches it.
export const withGalleryMimeType = (file: File): File => {
	const type = MIME_TYPES[getExtension(file.name)];
	return !type || file.type === type ? file : new File([file], file.name, { type, lastModified: file.lastModified });
};

const readHeader = (file: File, length: number): Promise<Uint8Array> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
		reader.onerror = () => reject(reader.error);
		reader.readAsArrayBuffer(file.slice(0, length));
	});

export const hasVideoSignature = async (file: File): Promise<boolean> => {
	const header = await readHeader(file, VIDEO_SIGNATURES[0].length);
	return VIDEO_SIGNATURES.some(signature => signature.every((byte, index) => header[index] === byte));
};

const isPictureDecodable = (file: File): Promise<boolean> =>
	new Promise(resolve => {
		const image = new Image();
		const url = URL.createObjectURL(file);
		const finish = (decodable: boolean) => {
			URL.revokeObjectURL(url);
			resolve(decodable);
		};
		image.onload = () => finish(image.naturalWidth > 0);
		image.onerror = () => finish(false);
		image.src = url;
	});

const isVideoPlayable = (file: File): Promise<boolean> =>
	new Promise(resolve => {
		const video = document.createElement("video");
		const url = URL.createObjectURL(file);
		let timer = 0;
		const finish = (playable: boolean) => {
			window.clearTimeout(timer);
			video.removeAttribute("src");
			video.load();
			URL.revokeObjectURL(url);
			resolve(playable);
		};
		timer = window.setTimeout(() => finish(false), VIDEO_CHECK_TIMEOUT);
		video.preload = "metadata";
		video.muted = true;
		video.onloadedmetadata = () => finish(video.duration > 0 && video.videoWidth > 0);
		video.onerror = () => finish(false);
		video.src = url;
	});

export interface GalleryFileCheck {
	// The file to send, with the type its extension needs.
	file: File;
	kind: GalleryMediaKind | null;
	// Why the file cannot be uploaded; undefined when it can.
	error?: string;
}

// Runs the backend's checks (format, size, content signature) before the upload, plus a decoding check: a broken
// file can still start with the right bytes, and a video the browser cannot read cannot be played on the site either.
export const checkGalleryFile = async (selected: File): Promise<GalleryFileCheck> => {
	const kind = getGalleryMediaKind(selected.name);
	if (!kind) {
		return { file: selected, kind, error: "Desteklenmeyen dosya biçimi. JPG, JPEG, PNG, GIF görsel ya da MP4 video seçin." };
	}

	const file = withGalleryMimeType(selected);
	if (file.size === 0) {
		return { file, kind, error: "Dosya boş." };
	}

	const limit = kind === "video" ? VolunteersGalleryRules.videoMaxFileSize : VolunteersGalleryRules.pictureMaxFileSize;
	if (file.size > limit) {
		return { file, kind, error: `Dosya boyutu sınırı aşıyor (${kind === "video" ? "video" : "görsel"} en fazla ${formatFileSize(limit)}).` };
	}

	try {
		if (kind === "video") {
			if (!(await hasVideoSignature(file)) || !(await isVideoPlayable(file))) {
				return { file, kind, error: "Video okunamadı ya da içeriği MP4 değil. Lütfen geçerli bir MP4 video seçin." };
			}
		} else if (!(await hasPictureSignature(file)) || !(await isPictureDecodable(file))) {
			return { file, kind, error: "Görsel okunamadı ya da içeriği dosya uzantısıyla uyuşmuyor. Lütfen geçerli bir JPG, PNG ya da GIF seçin." };
		}
	} catch {
		return { file, kind, error: "Dosya okunamadı." };
	}

	return { file, kind };
};

// The bytes the form adds around each file (boundary, headers) besides its name, and around the whole request; both
// are counted generously.
const FILE_PART_OVERHEAD = 512;
const REQUEST_OVERHEAD = 1024;

const getPartSize = (file: File) => file.size + FILE_PART_OVERHEAD + new Blob([file.name]).size;

// Splits the files, in their order, into requests the server accepts: at most 30 files and 30,000,000 bytes each.
export const splitIntoUploadBatches = (
	files: File[],
	maxBytes: number = VolunteersGalleryRules.maxRequestBytes,
	maxCount: number = VolunteersGalleryRules.maxFileCount
): File[][] => {
	const batches: File[][] = [];
	let current: File[] = [];
	let currentBytes = REQUEST_OVERHEAD;

	files.forEach(file => {
		const partSize = getPartSize(file);
		if (current.length > 0 && (currentBytes + partSize > maxBytes || current.length >= maxCount)) {
			batches.push(current);
			current = [];
			currentBytes = REQUEST_OVERHEAD;
		}
		current.push(file);
		currentBytes += partSize;
	});

	if (current.length > 0) {
		batches.push(current);
	}
	return batches;
};
